import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { AxiosResponse } from 'axios';
import { stats } from '@fiverr-private/obs';
import {
    BriefClassificationResponse,
    classifyBrief,
    getClassification,
    IntentClassification,
    LeafClassification,
} from '../../api';
import { reportTimeMetric } from '../../monitoring';
import { retryWrapper } from '../../utils';
import { METRICS_PREFIX, Step } from '../../constants';
import { useBriefCreationContext } from '../../context/BriefCreationContext';
import { logger } from '../../utils/logger';
import { checkClassificationResponseIsValid, handleClassificationError, isInvalidIntent } from './utils';
import { QUERY_RETRY_DELAY_MS } from './constants';
import { useIntentClassification } from './useIntentClassification';

export const CLASSIFY_USER_INPUT_TIMEOUT_MS = 12 * 1000;
const requestType = 'classify_user_input';

export const useClassifyUserInput = () => {
    const queryClient = useQueryClient();
    const { setStep, setBriefClassification, setIsOpen, contextId } = useBriefCreationContext();
    const { classifyIntent, isLoading: isClassifyingIntent } = useIntentClassification();
    const [isLoading, setIsLoading] = useState(false);

    const handleUserInputClassificationResult = ({
        classificationResponse,
        isAborted,
        userInput,
    }: {
        classificationResponse: AxiosResponse<BriefClassificationResponse>;
        isAborted: boolean;
        userInput: string;
    }) => {
        if (
            !checkClassificationResponseIsValid({
                classificationResponse,
                isAborted,
                requestType,
            })
        ) {
            return;
        }

        const { data: classification } = classificationResponse;

        const numberOfLeafClassifications = classification?.leaf_classification?.length;
        if (!numberOfLeafClassifications) {
            logger.warn(new Error('Failed while classifying user input to category - Rephrase step will be shown'), {
                userInput,
            });

            stats.count(`${METRICS_PREFIX}.${requestType}`, numberOfLeafClassifications === 0 ? 'rephrase' : 'error');
            setStep(numberOfLeafClassifications === 0 ? Step.Rephrase : Step.Error);
            return;
        }

        setBriefClassification(classification);

        if (classification.leaf_classification.length > 1) {
            setStep(Step.Disambiguation);
            setIsOpen(true);
            logger.info('Disambiguation step will be shown', { userInput });
            return;
        }

        stats.count(`${METRICS_PREFIX}.${requestType}`, 'success');
        return classification;
    };

    const classifyUserInput = async ({
        userInput,
        abortController,
    }: {
        userInput: string;
        abortController: AbortController;
    }): Promise<BriefClassificationResponse | undefined> => {
        const requestType = 'classify_user_input';
        const classificationStartTime = Date.now();
        return retryWrapper<BriefClassificationResponse | undefined>({
            retries: 2,
            metric: requestType,
            wrappedFn: async () => {
                try {
                    setIsLoading(true);
                    const {
                        data: { poll_id: classificationPollingId },
                    } = await queryClient.fetchQuery({
                        queryKey: [requestType, userInput],
                        queryFn: () =>
                            classifyBrief({
                                userInput,
                                contextId,
                                extractors: [],
                                abortController,
                            }),
                        cacheTime: 0,
                        retry: 2,
                    });

                    if (!classificationPollingId) {
                        handleClassificationError({ requestType, errorMessage: 'Failed to POST classifyUserInput' });
                        return;
                    }

                    if (!userInput) {
                        return;
                    }

                    const result = await queryClient.fetchQuery({
                        queryKey: [requestType, classificationPollingId],
                        queryFn: () =>
                            getClassification({
                                pollingId: classificationPollingId,
                                inspectedField: 'leaf_classification',
                                abortController,
                            }),
                        retry: CLASSIFY_USER_INPUT_TIMEOUT_MS / QUERY_RETRY_DELAY_MS,
                        retryDelay: QUERY_RETRY_DELAY_MS,
                    });

                    return handleUserInputClassificationResult({
                        classificationResponse: result,
                        isAborted: abortController.signal.aborted,
                        userInput,
                    });
                } catch (error: any) {
                    handleClassificationError({
                        requestType,
                        errorMessage: error.message,
                        isTimeout: error.message === 'No data',
                    });
                    throw error;
                } finally {
                    setIsLoading(false);
                    reportTimeMetric({
                        metricName: requestType,
                        time: Date.now() - classificationStartTime,
                        timeout: CLASSIFY_USER_INPUT_TIMEOUT_MS,
                    });
                }
            },
        });
    };

    const handleClassifyUserInput = async ({
        userInput,
        abortController,
    }: {
        userInput: string;
        abortController: AbortController;
    }): Promise<{
        classification?: BriefClassificationResponse;
        leafClassification?: LeafClassification;
        intentClassificationResult?: IntentClassification;
    }> => {
        const abortIntentClassification = new AbortController();
        let intentClassificationResult: IntentClassification | undefined;
        classifyIntent({
            userInput,
            abortController: abortIntentClassification,
        })
            .then((intentClassificationResponse) => {
                if (isInvalidIntent(intentClassificationResponse)) {
                    abortController.abort();
                    intentClassificationResult = intentClassificationResponse;
                }
            })
            .catch((error) => {
                logger.error(new Error('Failed to classify intent'), { error, userInput });
            });

        const classification = await classifyUserInput({ userInput, abortController });
        if (intentClassificationResult) {
            return {
                intentClassificationResult,
            };
        }

        abortIntentClassification.abort();
        return {
            classification,
            leafClassification: classification?.leaf_classification?.[0],
        };
    };

    return {
        handleClassifyUserInput,
        isLoading: isClassifyingIntent || isLoading,
    };
};
